import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Download, 
  RefreshCw, 
  History, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  FileJson, 
  ShieldCheck,
  Loader2,
  Trash2,
  Cloud
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BackupRecord, 
  getBackupHistory, 
  performBackup, 
  shouldTriggerAutoBackup, 
  getLastBackup,
  resetSystem,
  restoreBackup,
  performRemoteBackup,
  getRemoteBackupUrl,
  getBackupData
} from '../services/backupService';
import { auth, isQuotaError } from '../firebase';
import { AlertTriangle } from 'lucide-react';

const BackupManager: React.FC = () => {
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [remoteBackupStatus, setRemoteBackupStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasRemoteUrl, setHasRemoteUrl] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<BackupRecord | null>(null);
  const [lastBackup, setLastBackup] = useState<BackupRecord | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  useEffect(() => {
    fetchBackups();
    checkRemoteUrl();
  }, []);

  const checkRemoteUrl = async () => {
    const url = await getRemoteBackupUrl();
    setHasRemoteUrl(!!url);
  };

  const fetchBackups = async () => {
    setLoading(true);
    setQuotaExceeded(false);
    try {
      const history = await getBackupHistory(10);
      setBackups(history);
      if (history.length > 0) {
        setLastBackup(history[0]);
      }
    } catch (error) {
      if (isQuotaError(error)) {
        setQuotaExceeded(true);
      }
      console.error("Error fetching backups:", error);
    }
    setLoading(false);
  };

  const handleManualBackup = async () => {
    if (!auth.currentUser) return;
    setBackingUp(true);
    setQuotaExceeded(false);
    try {
      const result = await performBackup(auth.currentUser.uid);
      if (result) {
        await fetchBackups();
        
        // Trigger remote backup if configured
        if (hasRemoteUrl) {
          const rawData = await getBackupData(result);
          if (rawData) {
            const remoteSuccess = await performRemoteBackup(JSON.parse(rawData));
            setRemoteBackupStatus(remoteSuccess ? 'success' : 'error');
            setTimeout(() => setRemoteBackupStatus('idle'), 5000);
          }
        }
      } else {
        // Check if it was a quota error (performBackup returns null on quota error)
        // We can't easily check here without changing performBackup return type,
        // but we can assume if it failed it might be quota.
      }
    } catch (error) {
      if (isQuotaError(error)) {
        setQuotaExceeded(true);
      }
    }
    setBackingUp(false);
  };

  const downloadCurrentData = async () => {
    if (!auth.currentUser) return;
    setBackingUp(true);
    try {
      const result = await performBackup(auth.currentUser.uid);
      if (result) {
        downloadBackup(result);
        await fetchBackups();
      }
    } catch (error) {
      console.error("Download current data failed:", error);
      alert("שגיאה בהורדת הנתונים.");
    }
    setBackingUp(false);
  };

  const handleResetSystem = async () => {
    setResetting(true);
    const success = await resetSystem();
    if (success) {
      alert("המערכת אופסה בהצלחה. כל הנתונים נמחקו (פרט לגיבויים ומשתמשים).");
      window.location.reload();
    } else {
      alert("שגיאה באיפוס המערכת.");
    }
    setResetting(false);
    setShowResetConfirm(false);
  };

  const handleRestore = async (backup: BackupRecord) => {
    setRestoring(true);
    const success = await restoreBackup(backup);
    if (success) {
      alert("המערכת שוחזרה בהצלחה מהגיבוי.");
      window.location.reload();
    } else {
      alert("שגיאה בשחזור המערכת.");
    }
    setRestoring(false);
    setShowRestoreConfirm(null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !auth.currentUser) return;

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        
        // Basic validation
        if (!data.students && !data.staff && !data.classes) {
          throw new Error("קובץ לא תקין");
        }

        const confirmRestore = window.confirm("האם אתה בטוח שברצונך לשחזר את המערכת מקובץ זה? כל הנתונים הנוכחיים יימחקו.\n\nשים לב: אם מכסת השימוש היומית הסתיימה, השחזור עלול להיכשל ולהשאיר את המערכת ריקה.");
        if (!confirmRestore) {
          setUploading(false);
          return;
        }

        const backupRecord: BackupRecord = {
          timestamp: new Date(),
          createdBy: auth.currentUser!.uid,
          data: content,
          summary: {
            students: data.students?.length || 0,
            staff: data.staff?.length || 0,
            classes: data.classes?.length || 0,
            attendanceRecords: data.studentAttendance?.length || 0,
            staffAbsences: data.staffAbsences?.length || 0,
            transportRecords: data.transportAttendance?.length || 0,
            holidays: data.holidays?.length || 0
          }
        };

        const success = await restoreBackup(backupRecord);
        if (success) {
          alert("המערכת שוחזרה בהצלחה מהקובץ.");
          window.location.reload();
        } else {
          alert("שגיאה בשחזור מהקובץ.");
        }
      } catch (error) {
        console.error("File restore failed:", error);
        alert("שגיאה בקריאת הקובץ. וודא שזהו קובץ גיבוי תקין של המערכת.");
      }
      setUploading(false);
    };
    reader.readAsText(file);
    // Reset input
    event.target.value = '';
  };

  const downloadBackup = async (backup: BackupRecord) => {
    try {
      const rawData = await getBackupData(backup);
      if (!rawData) {
        alert("לא נמצאו נתוני גיבוי להורדה.");
        return;
      }
      const blob = new Blob([rawData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = backup.timestamp?.toDate ? backup.timestamp.toDate().toISOString() : new Date(backup.timestamp).toISOString();
      a.download = `school-backup-${dateStr.split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("שגיאה בהורדת קובץ הגיבוי.");
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'מעולם לא';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('he-IL');
  };

  const isAutoBackupNeeded = shouldTriggerAutoBackup(lastBackup);

  return (
    <div className="space-y-6" dir="rtl">
      {quotaExceeded && (
        <div className="bg-red-50 border border-red-200 p-6 rounded-3xl flex flex-col items-center text-center gap-4 animate-in fade-in slide-in-from-top-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-full">
            <AlertTriangle size={32} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-900">מכסת השימוש בבסיס הנתונים הסתיימה להיום</h3>
            <p className="text-red-700 text-sm mt-1 max-w-md">
              הגעת למגבלת הקריאות החינמית של Firestore. לא ניתן לבצע גיבויים או לשחזר נתונים כרגע. המכסה תתאפס מחר בבוקר.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <Database size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">גיבוי ושמירת נתונים</h2>
              <p className="text-sm text-slate-500">ניהול גיבויים אוטומטיים ושמירה על שלמות הנתונים</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <input
              type="file"
              id="backup-upload"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
            <label
              htmlFor="backup-upload"
              className={`flex items-center gap-2 bg-white border border-emerald-200 text-emerald-600 hover:bg-emerald-50 px-6 py-3 rounded-2xl transition-all font-bold shadow-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {uploading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={20} />}
              שחזר מקובץ חיצוני
            </label>

            {hasRemoteUrl && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${
                remoteBackupStatus === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                remoteBackupStatus === 'error' ? 'bg-red-50 text-red-700 border border-red-100' :
                'bg-indigo-50 text-indigo-700 border border-indigo-100'
              }`}>
                <Cloud size={16} />
                {remoteBackupStatus === 'success' ? 'גיבוי ענן הצליח' :
                 remoteBackupStatus === 'error' ? 'שגיאה בגיבוי ענן' :
                 'גיבוי ענן מוגדר'}
              </div>
            )}

            <button
              onClick={downloadCurrentData}
              disabled={backingUp}
              className="flex items-center gap-2 bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-6 py-3 rounded-2xl transition-all font-bold shadow-sm disabled:opacity-50"
            >
              {backingUp ? <Loader2 className="animate-spin" size={20} /> : <Download size={20} />}
              הורד נתונים למחשב
            </button>
            <button
              onClick={handleManualBackup}
              disabled={backingUp}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl transition-all font-bold shadow-lg shadow-indigo-100 disabled:opacity-50"
            >
              {backingUp ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={20} />}
              בצע גיבוי ענן
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3 mb-2 text-slate-500">
              <Clock size={18} />
              <span className="text-sm font-bold">גיבוי אחרון</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{formatDate(lastBackup?.timestamp)}</p>
          </div>

          <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3 mb-2 text-slate-500">
              <ShieldCheck size={18} />
              <span className="text-sm font-bold">סטטוס הגנה</span>
            </div>
            <div className="flex items-center gap-2">
              {isAutoBackupNeeded ? (
                <>
                  <AlertCircle className="text-amber-500" size={20} />
                  <span className="text-xl font-bold text-amber-600">נדרש גיבוי</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="text-emerald-500" size={20} />
                  <span className="text-xl font-bold text-emerald-600">מוגן</span>
                </>
              )}
            </div>
          </div>

          <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3 mb-2 text-slate-500">
              <History size={18} />
              <span className="text-sm font-bold">גיבויים שמורים</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{backups.length} גיבויים אחרונים</p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <History size={20} className="text-indigo-500" />
            היסטוריית גיבויים
          </h3>
          
          <div className="overflow-hidden border border-slate-100 rounded-2xl">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 text-sm font-bold text-slate-600">תאריך ושעה</th>
                  <th className="px-6 py-4 text-sm font-bold text-slate-600">סיכום נתונים</th>
                  <th className="px-6 py-4 text-sm font-bold text-slate-600">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center">
                      <Loader2 className="animate-spin mx-auto text-indigo-600" size={32} />
                    </td>
                  </tr>
                ) : backups.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center text-slate-400">
                      לא נמצאו גיבויים קודמים
                    </td>
                  </tr>
                ) : (
                  backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900">{formatDate(backup.timestamp)}</div>
                        <div className="text-xs text-slate-400">מזהה: {backup.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">
                            {backup.summary.students} תלמידים
                          </span>
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                            {backup.summary.staff} צוות
                          </span>
                          <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded text-xs">
                            {backup.summary.attendanceRecords} רשומות נוכחות
                          </span>
                          {backup.summary.holidays > 0 && (
                            <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded text-xs">
                              {backup.summary.holidays} חופשים
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => downloadBackup(backup)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            title="הורד גיבוי"
                          >
                            <Download size={20} />
                          </button>
                          <button
                            onClick={() => setShowRestoreConfirm(backup)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                            title="שחזר מגיבוי זה"
                          >
                            <RefreshCw size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-100 p-6 rounded-3xl flex items-start gap-4">
        <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
          <AlertCircle size={24} />
        </div>
        <div>
          <h4 className="font-bold text-amber-900 mb-1">מידע חשוב על גיבויים</h4>
          <p className="text-sm text-amber-800 leading-relaxed">
            המערכת מבצעת גיבוי אוטומטי פעם בשעה בעת כניסת מנהל או רענון המערכת. 
            <span className="font-bold">אפשרויות גיבוי נוספות:</span>
            <br />
            1. <span className="font-bold">גיבוי ענן:</span> נשמר בשרת המאובטח של גוגל (Firestore) בנפרד מהנתונים הפעילים.
            <br />
            2. <span className="font-bold">הורדה למחשב:</span> ניתן להוריד קובץ JSON המכיל את כל הנתונים וההגדרות למחשב האישי שלכם.
            מומלץ לבצע הורדה ידנית לפני שינויים משמעותיים או בסוף כל חודש לשמירה במקום חיצוני נוסף (כמו דיסק-און-קי או Google Drive אישי).
          </p>
        </div>
      </div>

      <AnimatePresence>
        {showRestoreConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" dir="rtl">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-100"
            >
              <div className="flex items-center gap-4 mb-6 text-emerald-600">
                <div className="p-3 bg-emerald-100 rounded-2xl">
                  <RefreshCw size={24} />
                </div>
                <h3 className="text-xl font-bold">שחזור מערכת מגיבוי</h3>
              </div>
              
              <div className="space-y-4 mb-8">
                <p className="text-slate-600 leading-relaxed">
                  האם אתה בטוח שברצונך לשחזר את המערכת לגיבוי מתאריך:
                  <span className="block font-bold text-slate-900 mt-1">{formatDate(showRestoreConfirm.timestamp)}</span>
                </p>
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 text-amber-800 text-sm">
                  <p className="font-bold mb-1 flex items-center gap-2">
                    <AlertCircle size={16} />
                    שים לב:
                  </p>
                  <p>פעולה זו תמחק את כל הנתונים הנוכחיים ותחליף אותם בנתונים מהגיבוי. אם מכסת השימוש היומית הסתיימה, השחזור עלול להיכשל באמצע.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => handleRestore(showRestoreConfirm)}
                  disabled={restoring}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-2xl font-bold transition-all shadow-lg shadow-emerald-100 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {restoring && <Loader2 className="animate-spin" size={18} />}
                  שחזר עכשיו
                </button>
                <button
                  onClick={() => setShowRestoreConfirm(null)}
                  disabled={restoring}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-2xl font-bold transition-all"
                >
                  ביטול
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="bg-red-50 border border-red-100 p-8 rounded-3xl">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-2xl">
            <Trash2 size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-red-900">איפוס מערכת</h3>
            <p className="text-sm text-red-700">מחיקת כל הנתונים מהמערכת (תלמידים, צוות, נוכחות וכו')</p>
          </div>
        </div>

        <p className="text-red-800 text-sm mb-6 leading-relaxed">
          פעולה זו תמחק את כל רשימות התלמידים, הצוות, הכיתות וכל היסטוריית הנוכחות. 
          <span className="font-bold underline mr-1">הגיבויים הקיימים ורשימת המשתמשים לא יימחקו</span>, כך שתוכל לשחזר את הנתונים במידת הצורך.
        </p>

        {!showResetConfirm ? (
          <button
            onClick={() => {
              setShowResetConfirm(true);
              setResetConfirmText('');
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-red-100"
          >
            איפוס כל הנתונים
          </button>
        ) : (
          <div className="flex flex-col gap-4 p-6 bg-white rounded-2xl border-2 border-red-200">
            <p className="font-bold text-red-600 flex items-center gap-2">
              <AlertCircle size={20} />
              האם אתה בטוח שברצונך למחוק את כל נתוני המערכת?
            </p>
            <div className="space-y-2">
              <p className="text-sm text-slate-600">להמשך, הקלד את המילה <span className="font-bold text-red-600">מחק</span>:</p>
              <input 
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="הקלד 'מחק' כאן"
                className="w-full max-w-xs px-4 py-2 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleResetSystem}
                disabled={resetting || resetConfirmText !== 'מחק'}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-xl font-bold disabled:opacity-50 flex items-center gap-2"
              >
                {resetting && <Loader2 className="animate-spin" size={18} />}
                כן, מחק הכל לצמיתות
              </button>
              <button
                onClick={() => {
                  setShowResetConfirm(false);
                  setResetConfirmText('');
                }}
                disabled={resetting}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-xl font-bold"
              >
                ביטול
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupManager;
